import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Spin, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { globalModel } from "../global/GlobalModel";
import { formatApiDateTime } from "../utils/backend";
import {
  type AccountDataScope,
  resolveAccountListFilter,
} from "./AccountDataScope";
import { accountTablePagination } from "./accountTableConfig";
import { MyBenefitsApi } from "./MyBenefitsApi";
import type { AppMyBenefitRow, ListMyBenefitsResult } from "./MyBenefitsType";

function formatProductLabel(row: AppMyBenefitRow): string {
  const sub = row.subProductName?.trim();
  if (sub) {
    return `${row.productName} · ${sub}`;
  }
  return row.productName;
}

type Props = {
  scope: AccountDataScope;
};

export const MyBenefitsPanel = observer((props: Props) => {
  const listFilter = useMemo(
    () => resolveAccountListFilter(props.scope),
    [props.scope]
  );
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AppMyBenefitRow[]>([]);

  const load = useCallback(async () => {
    if (!globalModel.isLoggedIn) return;
    setLoading(true);
    const res = await MyBenefitsApi.listMyBenefits(listFilter);
    setLoading(false);
    if (typeof res === "string") {
      return;
    }
    const data = res as ListMyBenefitsResult;
    setRows(data.list ?? []);
  }, [listFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: ColumnsType<AppMyBenefitRow> = [
    {
      title: "所属空间",
      dataIndex: "spaceName",
      key: "spaceName",
      ellipsis: true,
      width: 120,
    },
    {
      title: "产品名称",
      key: "productName",
      ellipsis: true,
      render: (_: unknown, r) => formatProductLabel(r),
    },
    {
      title: "最近获得时间",
      dataIndex: "benefitStartAt",
      key: "benefitStartAt",
      width: 250,
      render: (v) => formatApiDateTime(v) || "—",
    },
    {
      title: "到期时间",
      dataIndex: "benefitEndAt",
      key: "benefitEndAt",
      width: 250,
      render: (v) => formatApiDateTime(v),
    },
    {
      title: "剩余天数",
      dataIndex: "daysRemaining",
      key: "daysRemaining",
      width: 88,
      render: (v: number | null) => (v == null ? "—" : `${v} 天`),
    },
  ];

  if (!globalModel.isLoggedIn) {
    return <p className="my-orders-msg">请先登录后查看权益</p>;
  }

  return (
    <div className="my-benefits-panel">
      <Spin spinning={loading}>
        <Table
          rowKey={(r) =>
            `${r.spaceId}-${r.spaceProductId}-${r.subProductName || "default"}`
          }
          className="customer-account-table"
          columns={columns}
          dataSource={rows}
          pagination={accountTablePagination(rows.length)}
        />
      </Spin>
    </div>
  );
});
